import React, { useCallback, useMemo, useRef, useState } from 'react';
import { EmptyAttachmentsIcon } from '@icons';

import { Button, Spinner } from '@components/index';
import { STORAGE_KEYS } from '@constants/storage';
import FileUploadInput from '@features/lead-management/components/horizontal-stepper/FileUploadInput';
import LeadFileCard from '@features/lead-management/components/LeadFileCard';
import { UploadStatus } from '@features/lead-management/constants/common';
import {
  useGetLeadDetailsQuery,
  useLazyGetFileUrlQuery,
  useUpdateLeadMutation
} from '@features/lead-management/queries/LeadManagementQuery';
import useNotify from '@hooks/notify';
import { downloadFileFromUrlUsingBlob } from '@utils/downloadFile';
import { bytesToMB } from '@utils/file';
import { getLocalStorageItem } from '@utils/storage';

import DeleteConfirmationModal from './DeleteConfirmationModal';

import { useLeadFileUpload } from '../../hooks/useLeadFileUpload';
import { SelectedFileItem } from '../../types/leadFileUpload';

const AttachmentsTab: React.FC<{ leadId: string }> = ({ leadId }) => {
  const [selectedFile, setSelectedFile] = useState<SelectedFileItem | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const organizationId =
    getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID) || '';
  const { createSuccessAlert, createErrorAlert } = useNotify();

  const { data: leadDetails, isFetching } = useGetLeadDetailsQuery(
    { organizationId, leadId },
    { skip: !organizationId || !leadId, refetchOnMountOrArgChange: true }
  );

  const [getFileUrl] = useLazyGetFileUrlQuery();
  const [updateLead, { isLoading: isUpdating }] = useUpdateLeadMutation();

  const existingFiles: SelectedFileItem[] = useMemo(() => {
    if (!leadDetails?.documents) return [];
    return leadDetails.documents.map((doc: any) => ({
      id: doc.id,
      name: doc.file_name,
      sizeLabel: bytesToMB(Number(doc.file_size)),
      status: UploadStatus.COMPLETED,
      objectKey: doc.object_key
    }));
  }, [leadDetails]);

  const {
    items: uploadItems,
    isGettingUploadUrl,
    addFiles,
    removeItem,
    reset
  } = useLeadFileUpload({
    organizationId,
    onError: (message) => createErrorAlert(message),
    onBatchCompleted: async (completedItems) => {
      try {
        if (!leadDetails) return;
        const documentsToAdd = completedItems
          .filter((it) => it.objectKey)
          .map((it) => ({
            file_name: it.name,
            object_key: it.objectKey as string,
            file_size: (it.sizeBytes || 0).toString()
          }));
        if (documentsToAdd.length === 0) return;

        const updatePayload = {
          name: leadDetails.name,
          email_id: leadDetails.email_id,
          country_code: leadDetails.country_code,
          mobile_number: leadDetails.mobile_number,
          secondary_mobile_number: leadDetails.secondary_mobile_number || null,
          secondary_country_code: leadDetails.secondary_mobile_number
            ? leadDetails.secondary_country_code
            : null,
          city: leadDetails.city,
          state: leadDetails.state,
          category_id: leadDetails.category?.id || null,
          source: leadDetails.source,
          assignee_id: leadDetails.assignee?.id || null,
          status: leadDetails.status?.id,
          deal_value: leadDetails.deal_value,
          priority: leadDetails.priority,
          description: leadDetails.description,
          tags: (leadDetails.tags || []).map((t: any) => t.id),
          documents_to_add: documentsToAdd
        };

        await updateLead({
          organizationId,
          leadId,
          body: updatePayload
        }).unwrap();
        createSuccessAlert('Attachments uploaded successfully');
        reset();
      } catch (e) {
        createErrorAlert('Failed to save uploaded attachments');
      }
    }
  });

  const filesToRender: SelectedFileItem[] = useMemo(() => {
    const existingKeys = new Set(
      (leadDetails?.documents || []).map((d: any) => d.object_key)
    );
    const pendingOrUniqueUploads = uploadItems.filter(
      (u) =>
        !(
          u.status === UploadStatus.COMPLETED &&
          existingKeys.has(u.objectKey ?? u.id)
        )
    );
    return [...pendingOrUniqueUploads, ...existingFiles];
  }, [existingFiles, uploadItems, leadDetails]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || [];
    addFiles(files);
  };

  const onUploadDocuments = () => {
    fileInputRef.current?.click();
  };

  const getDocPrivateUrl = useCallback(
    async (documentId: string): Promise<string | undefined> => {
      try {
        const res = await getFileUrl({
          organizationId,
          documentId
        });
        return res.data?.url;
      } catch (err) {
        createErrorAlert('Failed to get file url');
        return undefined;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createErrorAlert, getFileUrl]
  );

  const handleDelete = (file: SelectedFileItem) => {
    setSelectedFile(file);
  };

  const handleDownload = useCallback(
    async (file: SelectedFileItem) => {
      try {
        const url = await getDocPrivateUrl(file.id);
        if (!url) throw new Error('No url');
        await downloadFileFromUrlUsingBlob(url, file.name);
        createSuccessAlert('File downloaded');
      } catch (err) {
        createErrorAlert('Failed to download file');
      }
    },
    [createErrorAlert, createSuccessAlert, getDocPrivateUrl]
  );

  const onConfirmDelete = async () => {
    try {
      if (!leadDetails) return;
      const updatePayload = {
        name: leadDetails.name,
        email_id: leadDetails.email_id,
        country_code: leadDetails.country_code,
        mobile_number: leadDetails.mobile_number,
        secondary_mobile_number: leadDetails.secondary_mobile_number || null,
        secondary_country_code: leadDetails.secondary_mobile_number
          ? leadDetails.secondary_country_code
          : null,
        city: leadDetails.city,
        state: leadDetails.state,
        category_id: leadDetails.category?.id || null,
        source: leadDetails.source,
        assignee_id: leadDetails.assignee?.id || null,
        status: leadDetails.status?.id,
        deal_value: leadDetails.deal_value,
        priority: leadDetails.priority,
        description: leadDetails.description,
        tags: (leadDetails.tags || []).map((t: any) => t.id),
        documents_to_delete: [selectedFile?.id]
      } as any;

      await updateLead({
        organizationId,
        leadId,
        body: updatePayload
      }).unwrap();
      createSuccessAlert('Attachment deleted');
      setSelectedFile(null);
    } catch (err) {
      createErrorAlert('Failed to delete attachment');
    }
  };

  const renderEmptyState = () => (
    <div className="py-25">
      <div className="flex flex-col items-center justify-center gap-6 py-8 text-center">
        <EmptyAttachmentsIcon />
        <p className="text-xl font-semibold text-darkGrey">
          You don’t have any attached <br /> document
        </p>
        <p className="text-sm font-normal leading-3 text-grey">
          Supported file: PDF, JPG and PNG (max. 5MB)
        </p>
        <Button
          label="Upload document"
          handleClick={onUploadDocuments}
          className="h-9 w-50 border border-[#00A657] !bg-[#EDFFF7] px-4 text-[#00A657]"
          variant="transparent"
          leftIconName="upload-cloud"
          leftIconStyle="!size-5"
          leftIconStroke="#05954E"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf, .jpg, .png"
          multiple
          className="hidden"
          onChange={handleFileInputChange}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className="relative flex flex-col gap-4 rounded-xl bg-white p-6">
        {(isFetching || isUpdating || isGettingUploadUrl) &&
        filesToRender?.length > 0 &&
        leadDetails?.documents?.length > 0 ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white bg-opacity-75">
            <Spinner />
          </div>
        ) : null}
        {filesToRender?.length > 0 && leadDetails?.documents?.length > 0 ? (
          <>
            <div className="mt-4 flex flex-col gap-4 bg-white">
              <FileUploadInput onFileChange={handleFileInputChange} />
            </div>

            <div className="mt-6 bg-white">
              <h4 className="mb-3 text-sm font-medium text-gray-700">
                Attached files
              </h4>
              <div className="max-h-125 space-y-3 overflow-y-auto">
                <LeadFileCard
                  files={filesToRender}
                  onRemove={(file) => removeItem(file.id)}
                  handleDownload={handleDownload}
                  handleDeleteNote={handleDelete}
                  showDeleteButton
                  showDownloadButton
                  showClose={false}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {isFetching || isUpdating || isGettingUploadUrl ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white bg-opacity-75">
                <Spinner />
              </div>
            ) : null}
            {renderEmptyState()}
          </>
        )}
      </div>
      <DeleteConfirmationModal
        showDeleteModal={!!selectedFile?.id}
        handleCancel={() => setSelectedFile(null)}
        handleConfirmDelete={onConfirmDelete}
        title="Delete attachment?"
        message="Are you sure you want to delete this attachment?"
        isConfirmButtonLoading={isUpdating}
      />
    </>
  );
};

export default AttachmentsTab;
